import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Heart, ArrowRight, LogOut } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { deviceId, SLOGANS } from "@/lib/feed";
import { CATEGORIES } from "@/constants/categories";
import { FeedCard } from "@/components/feed/FeedCard";
import { SloganCard, WantCard, StageCard } from "@/components/feed/Interstitials";
import { getHighlights } from "@/lib/stage";
import { StagePostViewer } from "@/components/stage/StagePostViewer";
import { FeedTabs, NewSincePill, InterestPicker, WelcomeStrip } from "@/components/feed/FeedChrome";
import { CampaignWelcome } from "@/components/CampaignWelcome";
import { LiveBriefingCard } from "@/components/LiveBriefing";

const PAGE = 10;

export default function Feed() {
  const { user, logout } = useAuth();
  const [params] = useSearchParams();
  const { productId: startId } = useParams();
  const cmp = params.get("cmp");
  const [tab, setTab] = useState("foryou");
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const reqRef = useRef(0);
  const [profile, setProfile] = useState(null);
  const [stagePosts, setStagePosts] = useState([]);
  const [stagePost, setStagePost] = useState(null);
  useEffect(() => { getHighlights(9).then(setStagePosts).catch(() => {}); }, []);
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem("vsk_feed_welcome_done"));
  const [showPicker, setShowPicker] = useState(() => !localStorage.getItem("vsk_feed_picker_done"));
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const [feedH, setFeedH] = useState("calc(100dvh - 64px)");

  useEffect(() => {
    const calc = () => {
      const main = document.getElementById("main-content");
      const top = main ? Math.round(main.getBoundingClientRect().top + window.scrollY) : 64;
      setFeedH(`calc(100dvh - ${Math.max(56, Math.min(top, 160))}px)`);
    };
    calc();
    const t1 = setTimeout(calc, 600);
    const t2 = setTimeout(calc, 2000);
    window.addEventListener("resize", calc);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener("resize", calc); };
  }, []);

  useEffect(() => {
    api.get(`/feed/profile?device_id=${deviceId()}`).then(({ data }) => setProfile(data)).catch(() => setProfile({}));
  }, [user]);

  const load = useCallback(async (reset = false) => {
    if (loadingRef.current && !reset) return;
    loadingRef.current = true;
    setLoading(true);
    const from = reset ? 0 : cursor;
    const forTab = tab;
    const myReq = reset ? ++reqRef.current : reqRef.current;
    try {
      const { data } = await api.get(`/feed?tab=${forTab}&cursor=${from}&limit=${PAGE}&device_id=${deviceId()}${startId && forTab !== "saved" ? `&start=${startId}` : ""}`);
      if (myReq !== reqRef.current) return;
      setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
      setCursor(data.next_cursor);
    } catch {
      setCursor(null);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [tab, cursor, startId]);

  useEffect(() => {
    setItems([]); setCursor(0);
    load(true);
    scrollRef.current?.scrollTo({ top: 0 });
  }, [tab, user, startId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || cursor === null) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) load(); }, { root: scrollRef.current, rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [load, cursor]);

  const onSaved = (id, saved) => {
    setProfile((p) => (p ? { ...p, saved_count: Math.max(0, (p.saved_count || 0) + (saved ? 1 : -1)) } : p));
    if (tab === "saved" && !saved) setItems((prev) => prev.filter((x) => x.id !== id));
  };

  const saveInterests = (cats) => {
    api.post("/feed/interests", { device_id: deviceId(), categories: cats }).then(() => {
      setProfile((p) => ({ ...(p || {}), interests: cats }));
    }).catch(() => {});
  };

  const display = useMemo(() => {
    const out = [];
    items.forEach((it, i) => {
      out.push({ kind: "product", item: it, key: it.id });
      const n = i + 1;
      if (tab !== "saved" && n % 5 === 0) out.push({ kind: "slogan", index: n / 5, key: `s${n}` });
      if (tab !== "saved" && n % 9 === 0) out.push({ kind: "want", index: n / 9, key: `w${n}` });
      if (tab !== "saved" && n % 7 === 0) out.push({ kind: "stage", index: n / 7, key: `st${n}` });
    });
    return out;
  }, [items, tab]);

  const topCats = (profile?.interests?.length ? profile.interests : profile?.top_categories) || [];

  return (
    <div data-testid="feed-page">
      {cmp && <CampaignWelcome code={cmp} />}
      <div className="max-w-7xl mx-auto md:px-6 lg:px-10 md:py-6">
        <div className="flex gap-8 justify-center">
          <aside className="hidden xl:flex flex-col gap-4 w-72 sticky top-24 h-fit" data-testid="feed-left-panel">
            <div className="rounded-2xl border border-[#27272A] bg-[#121212] p-5">
              <BrandMark />
              <h1 data-testid="feed-left-slogan" className="mt-3 font-black text-3xl leading-[0.95] tracking-tight uppercase text-[#C1121F]">A million products.<br />Always live.</h1>
              <p className="mt-3 text-sm text-zinc-400">Your feed learns what you love as you scroll. Nothing here ever expires.</p>
              <Link to="/welcome" data-testid="feed-left-welcome-link" className="mt-4 inline-flex items-center gap-1 text-sm text-[#007AFF] hover:underline">How VISMARKS works <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
            <Link to="/register?role=seller" data-testid="feed-left-sell-cta" className="rounded-2xl border border-[#FF3B30]/50 bg-[#FF3B30]/10 p-5 hover:bg-[#FF3B30]/15 transition-colors">
              <div data-testid="feed-left-sell-slogan" className="font-black text-3xl leading-[0.95] tracking-tight uppercase text-[#E5E7EB] [text-shadow:0_0_14px_rgba(255,255,255,0.45)]">Transform your store<br />into a global theatre</div>
              <p className="mt-1 text-sm text-zinc-300">Every listing stays always live until it sells. Founding sellers: first month free.</p>
            </Link>
          </aside>

          <div className="relative w-full md:max-w-lg">
            <div className="absolute md:static top-3 inset-x-0 z-10 flex flex-col items-center gap-2 px-3 md:px-0 md:mb-3 pointer-events-none">
              <div className="pointer-events-auto flex items-center gap-2">
                <FeedTabs tab={tab} onTab={setTab} savedCount={profile?.saved_count} />
                {user && user !== false && (
                  <button
                    type="button"
                    data-testid="home-logout-btn"
                    onClick={async () => { await logout(); window.location.assign("/"); }}
                    title={`Signed in as ${user.email}`}
                    className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-black/60 backdrop-blur text-xs font-semibold text-neutral-200 hover:bg-[#FF3B30]/20 hover:border-[#FF3B30]/50 hover:text-white transition-colors"
                  >
                    <LogOut className="h-3.5 w-3.5" /> Log out
                  </button>
                )}
              </div>
              <div className="pointer-events-auto">
                <NewSincePill count={tab === "foryou" ? profile?.new_since_last_visit || 0 : 0} onClick={() => { setTab("new"); setProfile((p) => ({ ...p, new_since_last_visit: 0 })); }} />
              </div>
            </div>

            <div ref={scrollRef} data-testid="feed-scroll" style={{ "--feed-h": feedH }} className="h-[var(--feed-h)] md:h-[calc(100dvh-140px)] overflow-y-auto snap-y snap-mandatory md:snap-proximity scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:space-y-6 md:rounded-2xl">
              {(showWelcome || showPicker) && tab === "foryou" && (
                <div className="snap-start space-y-3 p-3 pt-16 md:pt-0 md:p-0">
                  {showWelcome && <WelcomeStrip onDismiss={() => { localStorage.setItem("vsk_feed_welcome_done", "1"); setShowWelcome(false); }} />}
                  {showPicker && profile && <InterestPicker selected={profile.interests} onChange={saveInterests} onDismiss={() => { localStorage.setItem("vsk_feed_picker_done", "1"); setShowPicker(false); }} />}
                </div>
              )}
              {tab === "foryou" && (
                <div className={`snap-start p-3 md:p-0 ${showWelcome || showPicker ? "" : "pt-16 md:pt-0"}`} data-testid="feed-briefing-slot">
                  <LiveBriefingCard />
                </div>
              )}
              {display.map((d) => d.kind === "product" ? <FeedCard key={d.key} item={d.item} onSaved={onSaved} />
                : d.kind === "slogan" ? <SloganCard key={d.key} index={d.index} slogan={SLOGANS[(d.index - 1) % SLOGANS.length]} />
                : d.kind === "stage" ? <StageCard key={d.key} index={d.index} posts={stagePosts.length ? Array.from({ length: Math.min(3, stagePosts.length) }, (_, j) => stagePosts[((d.index - 1) * 3 + j) % stagePosts.length]) : []} onOpen={(p) => setStagePost(p.id)} />
                : <WantCard key={d.key} index={d.index} />)}
              {!loading && items.length === 0 && (
                <div className="h-[60vh] flex flex-col items-center justify-center text-center p-8" data-testid="feed-empty">
                  <Heart className="h-8 w-8 text-zinc-600" />
                  <div className="mt-3 font-bold text-lg">{tab === "saved" ? "Nothing saved yet" : "No products yet"}</div>
                  <p className="mt-1 text-sm text-zinc-500 max-w-xs">{tab === "saved" ? "Tap the heart on anything you like and it lands here." : "Be the first seller — your listing never expires."}</p>
                </div>
              )}
              {loading && <div className="p-6 text-center text-sm text-zinc-500" data-testid="feed-loading">Loading fresh picks…</div>}
              <div ref={sentinelRef} className="h-2" />
            </div>
          </div>

          <aside className="hidden lg:flex flex-col gap-4 w-80 sticky top-24 h-fit" data-testid="feed-right-panel">
            <div className="rounded-2xl border border-[#27272A] bg-[#121212] p-5">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Your feed is tuned to</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {topCats.length === 0 && <span className="text-sm text-zinc-500">Scroll, save and tap — we'll learn fast.</span>}
                {topCats.map((c) => <Link key={c} to={`/discover?category=${c}`} className="h-7 px-3 rounded-full border border-[#3F3F46] text-xs font-semibold text-zinc-200 hover:border-white/60 inline-flex items-center">{CATEGORIES.find((x) => x.slug === c)?.label || c}</Link>)}
              </div>
              {!showPicker && <button onClick={() => { localStorage.removeItem("vsk_feed_picker_done"); setShowPicker(true); scrollRef.current?.scrollTo({ top: 0 }); }} data-testid="feed-retune-btn" className="mt-4 text-xs text-[#007AFF] hover:underline">Re-tune my feed</button>}
            </div>
            <div className="rounded-2xl border border-[#27272A] bg-[#121212] p-5">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Buyers are asking for</div>
              <p className="mt-2 text-sm text-zinc-300">See live buyer wants and get matched when sellers list them.</p>
              <Link to="/open-demands" data-testid="feed-right-demands-link" className="mt-3 inline-flex items-center gap-1 text-sm text-[#007AFF] hover:underline">Open demand board <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </aside>
        </div>
      </div>
      <StagePostViewer postId={stagePost} onClose={() => setStagePost(null)} />
    </div>
  );
}
